import { NextLocation } from '@fmnts/api/next';
import {
  AsyncDataState,
  isAsyncDataAvailable,
  isAsyncErrorState,
} from '@fmnts/common/store';
import { isDefined, isNotNull, isNull } from '@fmnts/core';
import { selectRouteParam } from '@formunauts/shared/router/data-access';
import { createSelector } from '@ngrx/store';
import { Option, Struct, pipe } from 'effect';
import {
  asyncUploadEntityAdapter,
  nextLocationsAdapter,
  nextLocationsFeature,
} from './next-locations.reducer';

// Selectors from feature and adapter
export const {
  selectNextLocationsState,
  selectCheckIn,
  selectToday,
  selectPersistedCheckIns,
} = nextLocationsFeature;

export const {
  selectIds: selectNextLocationIds,
  selectEntities: selectNextLocationEntities,
  selectAll: selectAllNextLocations,
  selectTotal: selectNextLocationTotal,
} = nextLocationsAdapter.getSelectors(selectNextLocationsState);

// Selectors for Locations
export const {
  selectIds: selectLocationIds,
  selectEntities: selectLocationEntities,
  selectAll: selectAllLocations,
  selectTotal: selectLocationTotal,
} = asyncUploadEntityAdapter.getSelectors(selectPersistedCheckIns);

/**
 * ID of the location that the user is currently checked in.
 * If not checked in, value will be `null`.
 */
export const selectCurrentLocationId = createSelector(
  selectCheckIn,
  (checkIn) => checkIn?.locationId ?? null,
);

/**
 * Location that the user is currently checked in at.
 * - null: not checked in
 * - undefined: checked in, but no location details
 */
export const selectCurrentNextLocation = createSelector(
  selectCurrentLocationId,
  selectNextLocationEntities,
  (id, locations): NextLocation | null | undefined =>
    id === null ? null : locations[id],
);

/**
 * Coordinates of the location that the user is currently checked in at.
 * - null: no location or coordinates
 */
export const selectCurrentNextLocationCoordinates = createSelector(
  selectCurrentNextLocation,
  (value) =>
    pipe(value, Option.fromNullable, Option.map(Struct.get('coordinates'))),
);

/**
 * Current location and check in time.
 */
export const selectCurrentLocationCheckIn = createSelector(
  selectCheckIn,
  selectCurrentNextLocation,
  (checkIn, location) =>
    isNull(checkIn) || isNull(location)
      ? null
      : {
          timestamp: checkIn.timestamp,
          location: {
            ...location,
            id: checkIn.locationId,
          },
        },
);

/**
 * `true`, if the fundraiser is currently checked in at a location.
 */
export const selectIsCheckedIn = createSelector(
  selectCurrentLocationId,
  isNotNull,
);

/**
 * List of locations that the user can check in today.
 * `undefined` if not yet available.
 */
export const selectCurrentNextLocationsOrEmpty = createSelector(
  selectNextLocationIds,
  selectNextLocationEntities,
  (ids, entities) => ids?.map((id) => entities[id]) ?? undefined,
);

/**
 * List of locations that the user can check in today.
 */
export const selectCurrentNextLocations = createSelector(
  selectCurrentNextLocationsOrEmpty,
  (currentLocations) => currentLocations ?? [],
);

export const selectSelectedNextLocationId = selectRouteParam('nextLocationId');

export const selectSelectedNextLocation = createSelector(
  selectSelectedNextLocationId,
  selectNextLocationEntities,
  (nextLocationId, entities) =>
    nextLocationId ? entities[nextLocationId] : null,
);

/**
 * Get all locations that are already available
 */
export const selectAvailableCurrentNextLocations = createSelector(
  selectCurrentNextLocations,
  (locations) => locations.filter(isDefined),
);

export const selectRefresh = createSelector(selectToday, (today) =>
  isAsyncDataAvailable(today) ? today.refresh : null,
);

export const selectIsLoadingList = createSelector(
  selectToday,
  (today) => today.state === AsyncDataState.Loading,
);

export const selectLoadedLocations = createSelector(
  selectAllNextLocations,
  selectToday,
  (locations, today) =>
    today.state === AsyncDataState.Loaded || isAsyncErrorState(today.state)
      ? { locations }
      : undefined,
);

/**
 * Selects all the data needed for determining the location selection
 * state.
 * `undefined` as long as not all data is available.
 */
export const selectFundraiserLocationsState = createSelector(
  selectCurrentNextLocationsOrEmpty,
  selectAvailableCurrentNextLocations,
  selectCurrentNextLocation,
  (locationList, locations, currentLocation) => {
    if (
      locationList === undefined ||
      locationList.length !== locations.length ||
      currentLocation === undefined
    ) {
      return undefined;
    }

    return {
      locations,
      currentLocation,
    };
  },
);
